import { useState } from "preact/hooks";
import { wallTime } from "../clock.js";
import { newestFirst, type ViewPhoto } from "../photos.js";
import { MODERATION_TARGET } from "../seed.js";

export interface ModerationScreenProps {
  readonly photos: readonly ViewPhoto[];
  readonly hidden: readonly string[];
  onHide(id: string): void;
  onShow(id: string): void;
}

const STRIP = 5;

export function wallStrip(photos: readonly ViewPhoto[]): ViewPhoto[] {
  const newest = newestFirst(photos);
  const strip = newest.slice(0, STRIP);
  const target = newest.find((photo) => photo.id === MODERATION_TARGET);
  if (!target || strip.includes(target)) {
    return strip;
  }
  return [...strip.slice(0, STRIP - 1), target];
}

export function MiniWall({
  photos,
  hidden,
}: {
  readonly photos: readonly ViewPhoto[];
  readonly hidden: readonly string[];
}) {
  const strip = wallStrip(photos);
  const count = photos.length - hidden.length;
  return (
    <section class="d-mini-wall" data-theme="dark" aria-label="Telão agora" data-testid="mini-wall">
      <div class="d-mini-wall-head">
        <span class="d-micro">Telão agora</span>
        <span class="d-live">ao vivo</span>
        <span class="d-mini-wall-count">
          <strong key={count} data-testid="mini-wall-count">
            {count}
          </strong>{" "}
          fotos
        </span>
      </div>
      <ul class="d-mini-wall-strip">
        {strip.map((photo) => {
          const out = hidden.includes(photo.id);
          return (
            <li key={photo.id} class={out ? "is-out" : undefined}>
              <img src={photo.small} alt="" />
              {out ? <span data-testid="mini-wall-out">Saiu do telão</span> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function ModerationScreen({ photos, hidden, onHide, onShow }: ModerationScreenProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const hiddenPhotos = photos.filter((photo) => hidden.includes(photo.id));
  const shown = photos.filter((photo) => !hidden.includes(photo.id));
  const ordered = [
    ...shown.filter((photo) => photo.id === MODERATION_TARGET),
    ...newestFirst(shown.filter((photo) => photo.id !== MODERATION_TARGET)),
  ];
  const counts = new Map<string, number>();
  for (const photo of photos) {
    counts.set(photo.author, (counts.get(photo.author) ?? 0) + 1);
  }
  const people = [...counts.entries()].sort(([first], [second]) => first.localeCompare(second));
  return (
    <div class="d-screen d-scroll" data-theme="light" data-testid="screen-moderation">
      <main class="d-moderation">
        <h1 class="d-title">Moderação</h1>
        <MiniWall photos={photos} hidden={hidden} />
        <section>
          <p class="d-section-title">
            <span>Ocultas</span>
            <span>{hiddenPhotos.length}</span>
          </p>
          {hiddenPhotos.length === 0 ? (
            <p class="d-caption">Nenhuma foto oculta</p>
          ) : (
            <ul class="d-review">
              {hiddenPhotos.map((photo) => (
                <li key={photo.id} class="d-review-row">
                  <img class="d-review-thumb" src={photo.small} alt="" />
                  <div class="d-review-text">
                    <strong>{photo.author}</strong>
                    <span>{wallTime(photo.at)}</span>
                  </div>
                  <div class="d-review-actions">
                    <button
                      type="button"
                      class="d-button d-secondary d-small"
                      onClick={() => onShow(photo.id)}
                    >
                      Reexibir
                    </button>
                    <button type="button" class="d-button d-danger d-small" disabled>
                      Excluir
                    </button>
                    <button type="button" class="d-button d-ghost d-small" disabled>
                      Banir autor
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p class="d-caption">No evento real você também exclui a foto e bane o autor.</p>
        </section>
        <section>
          <p class="d-section-title">
            <span>Fotos do álbum</span>
            <span>{shown.length}</span>
          </p>
          <div class="d-mod-grid">
            {ordered.map((photo) => (
              <figure
                key={photo.id}
                class={photo.id === picked ? "d-mod-tile is-picked" : "d-mod-tile"}
              >
                <button
                  type="button"
                  class="d-mod-pick"
                  aria-label={`Selecionar foto de ${photo.author}`}
                  onClick={() => setPicked(photo.id === picked ? null : photo.id)}
                >
                  <img src={photo.small} alt="" />
                </button>
                {photo.id === MODERATION_TARGET ? (
                  <span class="d-mod-flag">Parece tremida</span>
                ) : null}
                {photo.id === MODERATION_TARGET || photo.id === picked ? (
                  <button
                    type="button"
                    class="d-mod-hide"
                    aria-label={`Ocultar foto de ${photo.author}`}
                    data-guide-target={photo.id === MODERATION_TARGET ? "moderation" : undefined}
                    onClick={() => onHide(photo.id)}
                  >
                    Ocultar
                  </button>
                ) : null}
              </figure>
            ))}
          </div>
        </section>
        <section>
          <p class="d-section-title">
            <span>Participantes</span>
            <span>{people.length}</span>
          </p>
          <ul class="d-people">
            {people.map(([name, count]) => (
              <li key={name}>
                <strong>{name}</strong>
                <span>
                  Convidado · Adulto · {count} {count === 1 ? "foto" : "fotos"}
                </span>
                <span class="d-badge is-muted">Ativo</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
